import { safeCallback } from '../socket-client/utils';
import { matchesShipmentId } from './utils';

/**
 * BaseHandler Class
 * Base class for socket handlers with common functionality
 * Reduces code duplication across different handler types
 */
class BaseHandler {
  constructor(socketClient, events, handlerName = 'BaseHandler') {
    if (!socketClient) {
      throw new Error(`${handlerName} requires a socketClient instance`);
    }
    
    this.socketClient = socketClient;
    this.events = events;
    this.handlerName = handlerName;
    this.currentShipmentId = null;
    this.unsubscribe = null;
    this.callback = null;
  }

  /**
   * Checks if socket is connected before proceeding
   */
  _checkConnection(action = 'operation') {
    if (!this.socketClient.isConnected()) {
      console.warn(`[${this.handlerName}] Socket not connected, cannot ${action}`);
      return false;
    }
    return true;
  }

  /**
   * Emits an event safely with error handling
   */
  _emitEvent(event, data) {
    this.socketClient.emit(event, data).catch((error) => {
      console.error(`[${this.handlerName}] Error emitting ${event} event:`, error);
    });
  }

  /**
   * Subscribes to socket events with shipment ID matching
   */
  _subscribe(event, shipmentId, callback) {
    return this.socketClient.subscribe(event, (data) => {
      if (matchesShipmentId(shipmentId, data)) {
        safeCallback(callback, [data], `[${this.handlerName}]`);
      }
    });
  }

  /**
   * Stops listening and cleans up resources
   */
  _cleanup() {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }

    if (this.currentShipmentId && this.socketClient.isConnected()) {
      this._emitEvent(this.events.DISCONNECT, { shipmentID: this.currentShipmentId });
    }

    this.currentShipmentId = null;
    this.callback = null;
  }

  /**
   * Stops listening - to be implemented by subclasses
   */
  stopListening() {
    this._cleanup();
    console.log(`[${this.handlerName}] Stopped listening`);
  }
}

export default BaseHandler;

